import { FetchUtil } from "../util/FetchUtil";
import { StringUtil } from "../util/StringUtil";
import { IGamificationViewModel } from "../models/Gamification/IGamificationViewModel";
import { IGamificationServiceEndpoint } from "../models/Gamification/IGamificationServiceEndpoint";
import { WyanokeUtil } from "../util/WyanokeUtil";
import { IGamificationViewModelStep } from "../models/Gamification/IGamificationViewModelStep";
import { EventUtil } from "../util/EventUtil";

export class GamificationService {
  private _currentStatePromise: Promise<IGamificationViewModel | null> | null = null;
  private _updatePromise: Promise<void> | null = null;
  private _state: IGamificationViewModel | null = null;

  constructor(private readonly _endpoint: IGamificationServiceEndpoint) {
  }

  public async currentState(): Promise<IGamificationViewModel | null> {
    const cachedPromise = this._currentStatePromise;

    console.info("Checking for current gamification state...");
    if (cachedPromise) {
      console.info("awaiting current promise...");
      await cachedPromise;
    }

    if(this._state){
      console.log("state exists, returning")
      return this._state;
    }

    if (cachedPromise){
      console.info("cached promise exists, returning");
      return cachedPromise;
    } else {
      console.info("no cached promise");
      this._currentStatePromise = this.loadCurrentState();
      return this._currentStatePromise
    }
  }

  public async updatePoints(stepid: string, points:number): Promise<void>{
    if (!this._state){
      await this.currentState();
      return this.updatePoints(stepid, points)
    }

    if(!this._state){
      throw new Error("No state!")
    }

    const stepIndex = this._state?.steps.findIndex(step => step.id === stepid);
    if(stepIndex === -1){
      throw new Error("Unable to find step: " + stepid);
    }

    if (this._updatePromise){
      await this._updatePromise;

      return this.updatePoints(stepid, points);
    }

    if(points > (this._state.steps[stepIndex].maxpoints ?? 0)){
      points = (this._state.steps[stepIndex].maxpoints ?? 0);
    }
    if (points > (this._state.steps[stepIndex].earnedpoints ?? 0)){

      this._state.steps[stepIndex].earnedpoints = points;
    }

    if(points === this._state.steps[stepIndex].maxpoints){
      let ev = EventUtil.createCustomEvent("gamification.changetext", { textId: "points-max"});
      window.document.dispatchEvent(ev);
    } else if(points < (this._state.steps[stepIndex].maxpoints ?? 0)){
      let ev = EventUtil.createCustomEvent("gamification.changetext", { textId: "points-missing"});
      window.document.dispatchEvent(ev);
    }

    return this._updatePromise = this.saveCurrentState();
  }

  private async saveCurrentState(): Promise<void>{
    console.info("Loading current gamification state...");
    const updateInProgress = this._updatePromise;
    if (updateInProgress) {
      console.info("Gamification update in progress, awaiting...");
      await updateInProgress;
      console.info("Gamification update complete");

      return this.saveCurrentState();
    }
    console.info("Saving gamification state as view model...");

    const activity = window.GamificationActivity;
    if (!activity || !activity.ActivityId) {
      console.warn("No global gamification activity context.");

      return;
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      return;
    }

    if(!this._state){
      return;
    }

    this._state.lastupdated = (new Date(Date.now())).toString();
    const body = {
      gamification: this._state
    };

    try{
      window.UserProfile.MasterClass = this._state;
    } catch(e: unknown){
      if(e instanceof Error) {
        console.log(e.message);
      }
    }

    await Promise.all([
      FetchUtil.sendMessage(this._endpoint.save, body)
    ]);

    this._updatePromise = null;
  }



  public async getRelatedContent(): Promise<string | null> {
    const endpoint = this._endpoint.loadall;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if(!profile){
      return null;
    }

    let contactId = profile.contactid || "";
    let values = {
      contactId,
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    }) as (IGamificationViewModel)[]);

    let ids = raw.filter(g => (g.badges.length ?? 0) > 0).map(g => g.activityid);

    const endpoint2 = this._endpoint.getrelatedcontent;
    if(!endpoint2){
      return null;
    }

    let query = "?seriesid=" + (window.GamificationActivity?.SeriesId ?? "") + "&completeids=" + ids.join("&completeids=");
    const url2 =
      (StringUtil.supplant(endpoint2.url, { query }) || "")
      .replace(/\{(?:query)\}/g, "");

    try {
      const result = await FetchUtil
      .sendMessage({ method: endpoint.method, url: url2 }, null);
      let card = result as {activitycard: string};
      return card.activitycard;

    } catch {
      console.log("Error in getting next activity");
    }

    return null;
  }

  public async updateBadges(): Promise<void> {

    if(!this._state?.badges){
      console.log("no badges, returning...")
      return;
    }

    let cme = window.GamificationBadges?.find(b => b.badgetype === "cme");
    if(!cme){
      console.log("no cme badge, returning...")
      return;
    }

    let badgeIndex = this._state?.badges.findIndex(b => {
      console.log(`Comparing: ${b.id}:${cme?.id}`);
      return b.id === cme?.id;
    });

    if (badgeIndex < 0){
      console.log("could not find badge, returning...")
      return;
    }

    let max = this._state?.steps.reduce((acc : number, step) => {
      acc += (step?.maxpoints ?? 0);
      return acc;
    }, 0);

    let earned = this._state?.steps.reduce((acc : number, step) => {
      acc += (step?.earnedpoints ?? 0);
      return acc;
    }, 0);

    console.log(`Earned: ${earned}/${max}`);
    if(max === earned){
      console.log("Swapping badge...");
      this._state?.badges.splice(badgeIndex, 1);
      this.awardMasterBadge(earned);
      this.saveCurrentState();
    }
  }

  public async awardBadges(): Promise<void> {
    const endpoint = this._endpoint.loadall;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if(!profile){
      return;
    }

    let contactId = profile.contactid || "";
    const values = {
      contactId,
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    }) as (IGamificationViewModel)[]);

    let completeActivities = raw.filter(g => g.badges.length > 0);
    let earned = this._state?.steps.map(s => s.earnedpoints ? s.earnedpoints : 0).reduce((acc, value) => acc + value, 0);
    let max = this._state?.steps.map(s => s.maxpoints ? s.maxpoints : 0).reduce((acc, value) => acc + value, 0);
    let quizearned = this._state?.steps.map(s => s.type === 2 ? s.earnedpoints ?? 0 : 0).reduce((acc, value) => acc + value, 0);
    let quizmax = this._state?.steps.map(s => s.type === 2 ? s.maxpoints ?? 0 : 0).reduce((acc, value) => acc + value, 0);


    //award on a roll
    if(raw.filter(g => g.activityid !== this._state?.activityid
                  && g.seriesid === this._state?.seriesid
                  && (g.badges?.length ?? 0) > 0).length === 1){
      let onaroll = window.GamificationBadges?.find(b => b.badgetype === "onaroll")?.id;
      if(onaroll){
        this._state?.badges.push({id: onaroll})
      }
    }

    //award perfectscore
    if(quizearned === quizmax){
      let perfectscore = window.GamificationBadges?.find(b => b.badgetype === "perfectscore")?.id;
      if(perfectscore){
        this._state?.badges.push({id: perfectscore})
      }
    }

    //award master badge
    if (earned !== max){
      //cme
      let cme = window.GamificationBadges?.find(b => b.badgetype === "cme")?.id;
      if(cme){
        this._state?.badges.push({id: cme})
      }

    } else {
      this.awardMasterBadge(earned ?? 0);
    }

    let numComplete = completeActivities.length + 1;
    if (numComplete === 5 ) {
      //award risingstar
      let risingstar = window.GamificationBadges?.find(b => b.badgetype === "risingstar")?.id;
      if(risingstar){
        this._state?.badges.push({id: risingstar})
      }
    } else if (numComplete === 10) {
      //award superstar
      let superstar = window.GamificationBadges?.find(b => b.badgetype === "superstar")?.id;
      if(superstar){
        this._state?.badges.push({id: superstar})
      }
    } else if (numComplete === 25 ) {
      //award diamond
      let diamond = window.GamificationBadges?.find(b => b.badgetype === "diamond")?.id;
      if(diamond){
        this._state?.badges.push({id: diamond})
      }
    }

    await this.saveCurrentState();
  }

  public async getActivityState(activityId: string): Promise<IGamificationViewModel | null> {
    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if(!profile){
      return null;
    }
    const contactId = profile.contactid || "";
    const endpoint = this._endpoint.load;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    }) as (IGamificationViewModel)[]);

    return raw[0];
  }

  private awardMasterBadge(earned: number) {
    if (! earned || earned < 500){
      //bronze
      let bronze = window.GamificationBadges?.find(b => b.badgetype === "bronze")?.id;
    if(bronze){
      this._state?.badges.push({id: bronze})
    }

    } else if (earned < 750) {
      //silver
      let silver = window.GamificationBadges?.find(b => b.badgetype === "silver")?.id;
      if(silver){
        this._state?.badges.push({id: silver})
      }

    } else if (earned < 1000) {
      //gold
      let gold = window.GamificationBadges?.find(b => b.badgetype === "gold")?.id;
      if(gold){
        this._state?.badges.push({id: gold})
      }

    } else {
      //platinum
      let platinum = window.GamificationBadges?.find(b => b.badgetype === "platinum")?.id;
      if(platinum){
        this._state?.badges.push({id: platinum})
      }
    }
  }

  private async loadCurrentState(): Promise<IGamificationViewModel | null>{
    console.info("Loading current gamification state...");
    const updateInProgress = this._updatePromise;
    if (updateInProgress) {
      console.info("Gamification update in progress, awaiting...");
      await updateInProgress;
      console.info("Gamification update complete");

      return this.loadCurrentState();
    }
    console.info("Returning gamification state as view model...");

    const activity = window.GamificationActivity;
    if (!activity || !activity.ActivityId) {
      console.warn("No global gamification activity context.");

      return null;
    }

    const profile = await WyanokeUtil.loadUserProfile(true, false);
    if (!profile) {
      this._state = {} as IGamificationViewModel;
      this._state.steps = window.GamificationActivity?.Steps?.map(s => {
        let step = {} as IGamificationViewModelStep;
        step.id = s.Id ? s.Id : "";
        step.locked = s.Locked ? s.Locked : false;
        step.maxpoints = s.MaxPoints ? s.MaxPoints : 0;
        step.earnedpoints = 0;
        step.type = s.Type ? Number(s.Type) : 0;
        return step;
      }).filter(Boolean) as IGamificationViewModelStep[];
      this._currentStatePromise = null;
      return this._state;
    }

    const contactId = profile.contactid || "";
    const activityId = activity.ActivityId;

    const endpoint = this._endpoint.load;

    if (!endpoint) {
      throw new Error("Could not resolve endpoint");
    }

    const values = {
      activityId,
      contactId,
      encodedActivityId: encodeURIComponent(activityId),
      encodedContactId: encodeURIComponent(contactId)
    };

    const url = StringUtil.supplant(endpoint.url, values) || "";

    const raw = (await FetchUtil.sendMessage({
      method: endpoint.method,
      url
    }) as (IGamificationViewModel)[]);


    this._state = {} as IGamificationViewModel;
    if(!raw[0]){
      this._state.contactid = window.UserProfile?.contactid ? window.UserProfile.contactid : "";
      this._state.activityid = window.Activity?.Id ? window.Activity.Id : "";
      this._state.seriesid = window.GamificationActivity?.SeriesId ? window.GamificationActivity.SeriesId : "";
      this._state.steps = window.GamificationActivity?.Steps?.map(s => {
        let step = {} as IGamificationViewModelStep;
        step.id = s.Id ? s.Id : "";
        step.locked = s.Locked ? s.Locked : false;
        step.maxpoints = s.MaxPoints ? s.MaxPoints : 0;
        step.earnedpoints = 0;
        step.type = s.Type ? Number(s.Type) : 0;
        return step;
      }).filter(Boolean) as IGamificationViewModelStep[];

      this._state.badges = [];

    } else {
      this._state = raw[0];
      this._state.seriesid = window.GamificationActivity?.SeriesId ? window.GamificationActivity.SeriesId : "";
      let localsteps = window.GamificationActivity?.Steps?.map(s => {
        let localstep = {} as IGamificationViewModelStep;
        let serverstep = this._state?.steps.find(st => st.id == s.Id);

        //load these from server
        localstep.earnedpoints = serverstep?.earnedpoints ? serverstep.earnedpoints : 0;
        localstep.locked = serverstep?.locked ? serverstep.locked : false;

        //load these from local (in case of updates)
        localstep.id = s.Id ? s.Id : "";
        localstep.maxpoints = s.MaxPoints ? s.MaxPoints : 0;
        localstep.type = s.Type ? Number(s.Type) : 0;
        return localstep;
      }).filter(Boolean) as IGamificationViewModelStep[];
      this._state.steps = localsteps;
    }


    this._currentStatePromise = null;
    await this.saveCurrentState();
    return this._state;
  }

}
